import { cleanAuthorizationParameters, isAuthorizationCallbackUrl } from "./authorization-url.js";
import { connectError } from "./errors.js";
/** URL-backed collection selection for browser and browser-shell applications. */
export class MdbaseBrowserSelection {
    collectionParameter;
    fallbackPath;
    listeners = new Set();
    handlePopState = () => this.emit();
    constructor(options = {}) {
        this.collectionParameter = options.collectionParameter ?? "collection";
        this.fallbackPath = options.fallbackPath ?? "/";
    }
    selectedCollectionId() {
        return this.currentUrl().searchParams.get(this.collectionParameter);
    }
    select(collectionId, options = {}) {
        const url = cleanAuthorizationParameters(this.currentUrl());
        if (collectionId)
            url.searchParams.set(this.collectionParameter, collectionId);
        else
            url.searchParams.delete(this.collectionParameter);
        const browserHistory = this.browserHistory();
        browserHistory[options.history === "push" ? "pushState" : "replaceState"](browserHistory.state, "", url);
        this.emit();
    }
    authorizationReturnTo() {
        const url = cleanAuthorizationParameters(this.currentUrl());
        return `${url.pathname}${url.search}${url.hash}`;
    }
    authorizationCallback() {
        const value = this.currentUrl().href;
        return isAuthorizationCallbackUrl(value) ? value : null;
    }
    finishAuthorization(returnTo, collectionId) {
        const url = this.safeAppUrl(returnTo ?? this.fallbackPath);
        cleanAuthorizationParameters(url);
        url.searchParams.set(this.collectionParameter, collectionId);
        const browserHistory = this.browserHistory();
        browserHistory.replaceState(null, "", url);
        this.emit();
    }
    clearAuthorizationCallback(returnTo) {
        const url = returnTo ? this.safeAppUrl(returnTo) : this.currentUrl();
        const browserHistory = this.browserHistory();
        browserHistory.replaceState(browserHistory.state, "", cleanAuthorizationParameters(url));
        this.emit();
    }
    subscribe(listener) {
        const first = this.listeners.size === 0;
        this.listeners.add(listener);
        if (first && typeof window !== "undefined") {
            window.addEventListener("popstate", this.handlePopState);
        }
        return () => {
            this.listeners.delete(listener);
            if (this.listeners.size === 0 && typeof window !== "undefined") {
                window.removeEventListener("popstate", this.handlePopState);
            }
        };
    }
    emit() {
        for (const listener of this.listeners)
            listener();
    }
    safeAppUrl(value) {
        const current = this.currentUrl();
        const candidate = new URL(value, current.origin);
        return candidate.origin === current.origin
            ? candidate
            : new URL(this.fallbackPath, current.origin);
    }
    currentUrl() {
        if (typeof location === "undefined") {
            throw connectError("browser_required", "Browser selection requires a browser environment.");
        }
        return new URL(location.href);
    }
    browserHistory() {
        if (typeof history === "undefined") {
            throw connectError("browser_required", "Browser selection requires a browser environment.");
        }
        return history;
    }
}
export class MdbaseMemorySelection {
    collectionId = null;
    listeners = new Set();
    selectedCollectionId() {
        return this.collectionId;
    }
    select(collectionId) {
        if (collectionId === this.collectionId)
            return;
        this.collectionId = collectionId;
        for (const listener of this.listeners)
            listener();
    }
    authorizationReturnTo() {
        return undefined;
    }
    authorizationCallback() {
        return null;
    }
    finishAuthorization(_returnTo, collectionId) {
        this.select(collectionId);
    }
    clearAuthorizationCallback() { }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
}
